import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';
import { Helpers } from 'src/app/models/helpers';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

@Component({
  selector: 'app-fornitore-list-accounts',
  standalone: false,
  templateUrl: './fornitore-list-accounts.component.html',
  styleUrls: ['./fornitore-list-accounts.component.scss'],
})


export class FornitoreListAccountsComponent implements OnInit {

  public readonly pageSize = 20;
  public readonly rowHeight = 44;

  // ── List ──────────────────────────────────────────────────────────────────
  public loading = false;
  public saving = false;
  public suppliers: FornitoriAdmin.ISupplierListItem[] = [];

  // ── Modal states ──────────────────────────────────────────────────────────
  public insertOpen = false;
  public newOpen = false;
  public detailOpen = false;
  public editOpen = false;
  public passwordOpen = false;

  // ── Selected supplier for detail / edit ───────────────────────────────────
  public selectedSupplier: FornitoriAdmin.ISupplierListItem | null = null;

  // ── "Inserisci fornitore" search flow ─────────────────────────────────────
  public findCode = '';
  public findLoading = false;
  public foundSupplier: FornitoriAdmin.FindSupplierResult | null = null;

  // ── New supplier form ─────────────────────────────────────────────────────
  public newForm = {
    ac_nome: '',
    ac_cognome: '',
    ac_password: '',
    ac_confirm_password: '',
    ac_note: '',
  };

  // ── Edit form ─────────────────────────────────────────────────────────────
  public editForm = {
    ac_nome: '',
    ac_cognome: '',
    ac_note: '',
    fornitore_mail: '',
  };

  // ── Password change ───────────────────────────────────────────────────────
  public newPassword = '';
  public confirmNewPassword = '';

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';
  public filterTerm = '';
  public selectedAccountt: string | null = null;
  public searchText = '';

  constructor(
    private readonly svc: FornitoriAdminMainService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadSuppliers();
  }

  public get gridHeight(): number {
    const rows = Math.max(this.filteredSuppliers.length, 1);
    return Math.min(368, Math.max(164, 44 + rows * this.rowHeight + 8));
  }

  // ── List ──────────────────────────────────────────────────────────────────

  loadSuppliers(): void {
    this.loading = true;
    this.svc.getSupplierAccounts().subscribe({
      next: (data) => {
        this.suppliers = data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Detail modal ──────────────────────────────────────────────────────────

  openDetail(supplier: FornitoriAdmin.ISupplierListItem): void {
    this.selectedSupplier = supplier;
    this.detailOpen = true;
  }

  closeDetail(): void {
    this.detailOpen = false;
    this.selectedSupplier = null;
  }

  // ── Edit modal ────────────────────────────────────────────────────────────

  openEdit(): void {
    if (!this.selectedSupplier) return;
    this.editForm = {
      ac_nome: this.selectedSupplier.ac_nome,
      ac_cognome: this.selectedSupplier.ac_cognome,
      ac_note: (this.selectedSupplier as any).ac_note ?? '',
      fornitore_mail: this.selectedSupplier.fornitore_mail ?? '',
    };
    this.detailOpen = false;
    this.editOpen = true;
  }

  closeEdit(): void {
    this.editOpen = false;
    this.selectedSupplier = null;
  }

  saveEdit(): void {
    if (!this.selectedSupplier) return;
    this.saving = true;
    const input = new FornitoriAdmin.SaveSupplierInput({
      id_fornitore: this.selectedSupplier.fornitore_id,
      account_id: this.selectedSupplier.account_id ?? 0,
      codice_fornitore: Number(this.selectedSupplier.codice_fornitore),
      fornitore: this.selectedSupplier.fornitore,
      fornitore_mail: this.editForm.fornitore_mail,
      fornitore_telefono: this.selectedSupplier.fornitore_telefono,
      ac_nome: this.editForm.ac_nome,
      ac_cognome: this.editForm.ac_cognome,
      ac_password: '',
      ac_note: this.editForm.ac_note,
    });
    this.svc.saveSupplier(input).subscribe({
      next: (res) => {
        this.saving = false;
        if (res.success) {
          this.editOpen = false;
          this.loadSuppliers();
          Swal.fire({
            icon: 'success',
            title: 'Salvato!',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false
          });
        }
        else {
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: res.error_message?.msg_text || 'Operazione fallita.'
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.saving = false;
        Swal.fire({
          icon: 'error',
          title: 'Errore',
          text: 'Si è verificato un errore.'
        });
        this.cdr.detectChanges();
      },
    });
  }

  // ── Status toggle ─────────────────────────────────────────────────────────

  toggleStatus(): void {
    if (!this.selectedSupplier) return;
    const isActive = this.selectedSupplier.ac_attivo === 'S';
    Swal.fire({
      title: isActive ? 'Disattivare il fornitore?' : 'Attivare il fornitore?',
      text: isActive ? 'Dovrai riattivarlo per renderlo operativo' : 'Il fornitore sarà di nuovo operativo',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '✔ SI',
      cancelButtonText: '✖ NO',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#dc2626',
    }).then(result => {
      if (!result.isConfirmed || !this.selectedSupplier) return;
      const input = new FornitoriAdmin.StatusSupplierInput({
        id_fornitore: this.selectedSupplier.fornitore_id,
        account_id: this.selectedSupplier.account_id ?? 0,
        ac_attivo: isActive ? 'N' : 'S',
      });
      this.svc.statusSupplier(input).subscribe({
        next: (res) => {
          if (res.success) {
            const msg = isActive ? 'Fornitore disattivato!' : 'Fornitore riattivato!';
            this.editOpen = false;
            this.loadSuppliers();
            Swal.fire({
              icon: 'success',
              title: msg,
              timer: 1500,
              timerProgressBar: true,
              showConfirmButton: false
            });
          }
          else {
            Swal.fire({
              icon: 'error',
              title: 'Errore',
              text: res.error_message?.msg_text || 'Operazione fallita.'
            });
          }
          this.cdr.detectChanges();
        },
        error: () => Swal.fire({ icon: 'error', title: 'Errore', text: 'Si è verificato un errore.' }),
      });
    });
  }

  // ── Password change ───────────────────────────────────────────────────────

  openPassword(): void {
    this.newPassword = '';
    this.confirmNewPassword = '';
    this.passwordOpen = true;
  }

  savePassword(): void {
    if (!this.selectedSupplier) return;
    if (!this.newPassword) {
      Swal.fire({ icon: 'warning', title: 'Attenzione', text: 'Inserisci la nuova password.' });
      return;
    }
    if (this.newPassword !== this.confirmNewPassword) {
      Swal.fire({ icon: 'error', title: 'Errore', text: 'Le password non coincidono.' });
      return;
    }
    this.saving = true;
    const input = new FornitoriAdmin.SaveSupplierInput({
      id_fornitore: this.selectedSupplier.fornitore_id,
      account_id: this.selectedSupplier.account_id ?? 0,
      codice_fornitore: Number(this.selectedSupplier.codice_fornitore),
      fornitore: this.selectedSupplier.fornitore,
      fornitore_mail: this.selectedSupplier.fornitore_mail,
      fornitore_telefono: this.selectedSupplier.fornitore_telefono,
      ac_nome: this.selectedSupplier.ac_nome,
      ac_cognome: this.selectedSupplier.ac_cognome,
      ac_password: this.newPassword,
      ac_note: '',
    });
    this.svc.saveSupplier(input).subscribe({
      next: (res) => {
        this.saving = false;
        if (res.success) {
          this.passwordOpen = false;
          Swal.fire({
            icon: 'success',
            title: 'Password aggiornata!',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false
          });
        }
        else {
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: res.error_message?.msg_text || 'Operazione fallita.'
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.saving = false;
        Swal.fire({ 
          icon: 'error', 
          title: 'Errore', 
          text: 'Si è verificato un errore.' 
        });
      },
    });
  }

  // ── Add new supplier flow ─────────────────────────────────────────────────

  openInsert(): void {
    this.findCode = '';
    this.foundSupplier = null;
    this.insertOpen = true;
  }

  clearSearch(): void {
    this.searchText = '';
  }

  /**
   * 🚨 FIX v2.12.4 (B2): pre-v2.12.4 il codice user-typed veniva passato
   * direttamente a `Number(...)` senza validazione. Se l'utente
   * inserisce caratteri non numerici (es. "F001" o leading whitespace),
   * `Number()` ritorna `NaN` → URL `/find_supplier/NaN/` → BE 404 →
   * popup "Non trovato" anche per codici esistenti.
   * Fix: validare che sia un intero positivo prima di chiamare il
   * service. Combinato con la normalizzazione array/oggetto nel
   * service (vedi `findSupplier` in `fornitori-admin-main.service.ts`).
   */
  findSupplier(): void {
    const raw = (this.findCode ?? '').toString().trim();
    if (!raw) return;
    const codeNum = Number(raw);
    if (!Number.isFinite(codeNum) || codeNum <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Codice non valido',
        text: 'Inserisci un codice numerico positivo.',
      });
      return;
    }
    this.findLoading = true;
    this.svc.findSupplier(codeNum).subscribe({
      next: (result) => {
        this.findLoading = false;
        if (result?.codice_fornitore) {
          this.foundSupplier = result;
          this.newForm = { ac_nome: '', ac_cognome: '', ac_password: '', ac_confirm_password: '', ac_note: '' };
          this.insertOpen = false;
          this.newOpen = true;
        }
        else {
          Swal.fire({
            icon: 'error',
            title: 'Non trovato',
            text: 'Il fornitore cercato non esiste.'
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.findLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Non trovato',
          text: 'Il fornitore cercato non esiste.'
        });
        this.cdr.detectChanges();
      },
    });
  }

  createSupplier(): void {
    if (!this.foundSupplier) return;
    if (!this.newForm.ac_nome || !this.newForm.ac_cognome || !this.newForm.ac_password) {
      Swal.fire({ 
        icon: 'warning', 
        title: 'Attenzione', 
        text: 'Inserisci le informazioni mancanti.' 
      });
      return;
    }
    if (this.newForm.ac_password !== this.newForm.ac_confirm_password) {
      Swal.fire({ 
        icon: 'error', 
        title: 'Errore', 
        text: 'Le password non coincidono.' 
      });
      return;
    }
    this.saving = true;
    const input = new FornitoriAdmin.SaveSupplierInput({
      id_fornitore: 0,
      account_id: 0,
      codice_fornitore: this.foundSupplier.codice_fornitore,
      fornitore: this.foundSupplier.fornitore,
      fornitore_mail: this.foundSupplier.fornitore_mail,
      fornitore_telefono: this.foundSupplier.fornitore_telefono,
      ac_nome: this.newForm.ac_nome,
      ac_cognome: this.newForm.ac_cognome,
      ac_password: this.newForm.ac_password,
      ac_note: this.newForm.ac_note,
    });
    this.svc.saveSupplier(input).subscribe({
      next: (res) => {
        this.saving = false;
        if (res.success) {
          this.newOpen = false;
          this.foundSupplier = null;
          this.loadSuppliers();
          Swal.fire({
            icon: 'success',
            title: 'Fornitore aggiunto!',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false
          });
        }
        else {
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: res.error_message?.msg_text || 'Operazione fallita.'
          });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.saving = false;
        Swal.fire({ 
          icon: 'error', 
          title: 'Errore', 
          text: 'Si è verificato un errore.' 
        });
      },
    });
  }

  closeNew(): void {
    this.newOpen = false;
    this.foundSupplier = null;
  }

  isActive(s: FornitoriAdmin.ISupplierListItem | null): boolean {
    return s?.ac_attivo === 'S';
  }

  private haystack(supplier: FornitoriAdmin.ISupplierListItem): string {
      return [
        supplier.account_id,
        supplier.ac_nome,
        supplier.ac_cognome,
        supplier.ac_attivo,
        supplier.fn_attivo,
        supplier.codice_fornitore,
        supplier.fornitore,
        supplier.fornitore_mail,
        supplier.fornitore_telefono,
        supplier.ac_data_scadenza
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    }

  get filteredSuppliers(): FornitoriAdmin.ISupplierListItem[] {
  const q = this.searchText.trim().toLowerCase();

  return this.suppliers.filter((sup) => {
    if (sup.account_id === Helpers.PermisionManufacturer) return false;
    if (!q) return true;
    return this.haystack(sup).includes(q);
  });
}
  
  get sortedSuppliers(): FornitoriAdmin.ISupplierListItem[] {
  if (!this.sortColumn) return this.filteredSuppliers;

  return sortItems(
    this.filteredSuppliers,
    this.sortColumn,
    this.sortDirection
  );
}

get pagedSuppliers(): FornitoriAdmin.ISupplierListItem[] {
  const start = 0; // o currentPage * pageSize se ce l’hai
  const size = this.pageSize;

  return this.sortedSuppliers.slice(start, start + size);
}

sortBy(column: string): void {
  const next = nextSortState(this.sortColumn, this.sortDirection, column);

  this.sortColumn = next.column;
  this.sortDirection = next.direction;

  this.cdr.detectChanges();
}
  
}
